import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, ResponsiveContainer } from "recharts";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GlassCard, Ring, Bar, Chip, Skeleton } from "@/v2/components/ui";
import { AV_FALLBACK } from "@/components/Widgets";
import ProfileSettings from "@/v2/components/ProfileSettings";
import { levelInfo, skillRadar, LEVELS } from "@/v2/lib/gamification";
import {
  Star, CheckCircle2, Flame, Building2, Hash, Mail, Briefcase, Award, Lock,
  LogIn, Zap, Milestone, Crown, Trophy, Activity, Settings,
} from "lucide-react";

const ICONS = { LogIn, Award, Flame, Zap, Milestone, Crown, Trophy };

export default function Profile() {
  const { user } = useAuth();
  const [settings, setSettings] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ["profile"], queryFn: async () => (await api.get("/profile")).data });
  if (isLoading || !data) return <div className="flex justify-center py-24"><Skeleton className="h-64 w-full" /></div>;

  const u = data.user;
  const lvl = levelInfo(u.points || 0);
  const completed = u.completed_challenge_ids?.length || 0;
  const pct = Math.round((completed / (data.total_challenges || 20)) * 100);
  const radar = skillRadar(u);
  const bestScore = data.attempts.reduce((m, a) => Math.max(m, a.score || 0), 0);

  return (
    <div className="space-y-6">
      {/* hero */}
      <GlassCard gold className="relative overflow-hidden v2-pattern p-6 sm:p-8">
        <button onClick={() => setSettings(true)} data-testid="profile-settings-btn" title="إعدادات الحساب"
          className="absolute top-4 left-4 z-20 w-11 h-11 rounded-full v2-inset flex items-center justify-center text-[#bcc8cf] hover:text-[#e3c799] hover:border-[#bb9661]/50 transition-colors">
          <Settings size={19} />
        </button>
        <div className="relative z-10 flex flex-col sm:flex-row items-center gap-6">
          <button onClick={() => setSettings(true)} data-testid="profile-avatar-btn" className="group relative">
            <Ring value={lvl.progress} size={140} stroke={12} id="profring">
              <img src={u.avatar} alt={u.name} onError={AV_FALLBACK} data-testid="profile-avatar"
                className="w-24 h-24 rounded-full object-cover border-2 border-[#bb9661]/50" />
            </Ring>
            <span className="absolute inset-0 rounded-full flex items-center justify-center bg-[#25343f]/60 opacity-0 group-hover:opacity-100 transition-opacity text-xs font-bold">
              تغيير الصورة
            </span>
          </button>
          <div className="flex-1 text-center sm:text-start">
            <h1 className="text-3xl font-extrabold" data-testid="profile-name">{u.name}</h1>
            <p className="text-[#bcc8cf]">{u.job_title} — {u.department}</p>
            <div className="flex items-center gap-2 justify-center sm:justify-start mt-2 flex-wrap">
              <Chip tone="gold"><Crown size={12} /> {lvl.current.name}</Chip>
              <Chip tone="emerald"><Star size={12} /> {u.points} XP</Chip>
            </div>
            <div className="mt-4 max-w-sm mx-auto sm:mx-0">
              <div className="flex justify-between text-xs text-[#bcc8cf] mb-1"><span>المستوى التالي</span><span>{lvl.next ? `${lvl.toNext} نقطة` : "الأعلى"}</span></div>
              <Bar value={lvl.progress} />
            </div>
          </div>
        </div>
      </GlassCard>

      {/* quick facts */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[["النقاط", u.points, Star], ["مكتمل", completed, CheckCircle2], ["أيام متتالية", u.streak, Flame], ["أفضل نتيجة", bestScore, Trophy]].map(([l, v, I]) => (
          <GlassCard key={l} hover className="p-5 text-center"><I className="mx-auto text-[#e3c799]" size={22} /><div className="text-2xl font-extrabold mt-1">{v}</div><div className="text-xs text-[#bcc8cf]">{l}</div></GlassCard>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* radar */}
        <GlassCard className="p-5">
          <h3 className="font-bold mb-2 flex items-center gap-2"><Activity size={16} className="text-[#91cab8]" /> المهارات المطوّرة</h3>
          <ResponsiveContainer width="100%" height={280}>
            <RadarChart data={radar} outerRadius="72%">
              <PolarGrid stroke="rgba(255,255,255,0.12)" />
              <PolarAngleAxis dataKey="skill" tick={{ fill: "#bcc8cf", fontSize: 12 }} />
              <Radar dataKey="value" stroke="#91cab8" fill="#91cab8" fillOpacity={0.35} />
            </RadarChart>
          </ResponsiveContainer>
        </GlassCard>

        {/* identity + info */}
        <GlassCard className="p-5">
          <h3 className="font-bold mb-3">بيانات الموظف</h3>
          <div className="space-y-3 text-sm text-[#bcc8cf]">
            <div className="flex items-center gap-2"><Building2 size={16} className="text-[#91cab8]" /> {u.department}</div>
            <div className="flex items-center gap-2"><Briefcase size={16} className="text-[#91cab8]" /> {u.job_title}</div>
            <div className="flex items-center gap-2"><Hash size={16} className="text-[#91cab8]" /> {u.employee_id}</div>
            <div className="flex items-center gap-2"><Mail size={16} className="text-[#91cab8]" /> {u.email}</div>
          </div>
          <div className="mt-4 pt-4 border-t border-white/10">
            <div className="flex justify-between text-xs text-[#bcc8cf] mb-1"><span>إكمال البرنامج</span><span>{pct}%</span></div>
            <Bar value={pct} />
          </div>
          <div className="mt-4 flex items-center gap-1">
            {LEVELS.map((L, i) => <div key={i} className={`flex-1 h-1.5 rounded-full ${i <= lvl.index ? "bg-gradient-to-l from-[#91cab8] to-[#bb9661]" : "bg-white/10"}`} />)}
          </div>
        </GlassCard>
      </div>

      {/* badges */}
      <GlassCard className="p-5">
        <h3 className="font-bold mb-4 flex items-center gap-2"><Award size={16} className="text-[#e3c799]" /> الشارات والإنجازات</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {data.badges.map((b) => { const I = ICONS[b.icon] || Award; return (
            <div key={b.id} className={`rounded-2xl border p-4 text-center ${b.earned ? "border-[#bb9661]/40 bg-[#bb9661]/5" : "border-white/10 opacity-55"}`}>
              <div className={`w-12 h-12 rounded-2xl mx-auto flex items-center justify-center ${b.earned ? "bg-gradient-to-br from-[#e3c799] to-[#bb9661] text-[#25343f] v2-glow-gold" : "bg-white/5 text-[#bcc8cf]"}`}>{b.earned ? <I size={22} /> : <Lock size={18} />}</div>
              <div className="mt-2 text-xs font-semibold">{b.name}</div>
            </div>
          ); })}
        </div>
      </GlassCard>

      {/* history */}
      <div className="grid lg:grid-cols-2 gap-6">
        <GlassCard className="p-5">
          <h3 className="font-bold mb-3">النشاط الأخير</h3>
          {data.activity.length === 0 ? <p className="text-sm text-[#bcc8cf]">لا يوجد نشاط بعد.</p> :
            <ul className="space-y-2">{data.activity.slice(0, 8).map((a, i) => <li key={i} className="flex justify-between text-sm border-b border-white/5 pb-2"><span className="text-[#bcc8cf]">{a.type === "checkin" ? "تسجيل حضور يومي" : "إكمال تحدٍ"}</span><span className="v2-gold-text font-bold">+{a.points}</span></li>)}</ul>}
        </GlassCard>
        <GlassCard className="p-5">
          <h3 className="font-bold mb-3">سجل نتائج التحديات</h3>
          {data.attempts.length === 0 ? <p className="text-sm text-[#bcc8cf]">لم تُجرِ اختبارًا بعد.</p> :
            <ul className="space-y-2">{data.attempts.slice(0, 8).map((a) => <li key={a.id} className="flex justify-between text-sm border-b border-white/5 pb-2"><span className="text-[#bcc8cf]">محاولة اختبار</span><span className={`font-bold ${a.passed ? "text-[#91cab8]" : "text-[#F7A08A]"}`}>{a.score}/100 {a.passed ? "✓" : ""}</span></li>)}</ul>}
        </GlassCard>
      </div>
      {settings && <ProfileSettings onClose={() => setSettings(false)} />}
    </div>
  );
}
